import { useNavigate } from "react-router-dom";
import { useGetResearcherProgramsQuery } from "../../utils/api/endpoints/checklists/researcherChecklistApi";
import { FiClock, FiFolder, FiLayers, FiArrowRight, FiCheckCircle } from "react-icons/fi";
import { sanitizeHtml } from "../../utils/sanitizers";

const ResearcherChecklistProgramsPage = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetResearcherProgramsQuery();

  const programs = data?.data || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center animate-in fade-in duration-700">
          <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm font-medium text-slate-500 tracking-wide">Syncing programs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 p-6">
        <div className="text-center max-w-xs bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <div className="w-12 h-12 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
             <FiCheckCircle className="rotate-45" />
          </div>
          <h3 className="text-sm font-bold text-slate-900 mb-1">Load Failed</h3>
          <p className="text-xs text-slate-500 mb-6 font-medium">We couldn't retrieve your assigned programs at this time.</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full py-2.5 bg-slate-900 text-white text-[11px] font-black uppercase tracking-widest rounded-lg hover:bg-slate-800 transition-all active:scale-95"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  if (programs.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center max-w-xs">
          <div className="w-16 h-16 bg-white rounded-2xl shadow-sm border border-slate-200 flex items-center justify-center mx-auto mb-6">
            <FiFolder className="w-6 h-6 text-slate-400" />
          </div>
          <h3 className="text-base font-bold text-slate-900 mb-2 tracking-tight">No Active Assignments</h3>
          <p className="text-xs text-slate-500 leading-relaxed font-medium">
            Your workspace is currently empty. Assigned run-specific checklists will appear here automatically.
          </p>
        </div>
      </div>
    );
  }

  // Group run checklists by program_id
  const runChecklistsByProgram = programs.reduce((acc, run) => {
    if (!acc[run.program_id]) {
      acc[run.program_id] = {
        title: run.program_title,
        runs: []
      };
    }
    acc[run.program_id].runs.push(run);
    return acc;
  }, {} as Record<number, { title: string; runs: any[] }>);

  const renderProgramCard = (program: any) => {
    const progress = program.total_items > 0 ? Math.round((program.completed_items / program.total_items) * 100) : 0;
    
    return (
      <div
        key={`${program.program_id}-${program.run_id || 'master'}`}
        onClick={() => {
          const url = `/dashboard/researcher-checklists/programs/${program.program_id}?runId=${program.run_id}`;
          navigate(url);
        }}
        className="group bg-white rounded-xl border border-slate-200 p-5 cursor-pointer hover:border-slate-400 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 relative overflow-hidden"
      >
        {/* Company Badge */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-[9px] font-black uppercase tracking-[0.15em] text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100/50">
            {program.company_name || 'System'}
          </span>
          <div className={`p-1.5 rounded-lg ${program.run_id ? 'bg-slate-50 text-slate-400' : 'bg-emerald-50 text-emerald-500'}`}>
            {program.run_id ? <FiClock className="w-3.5 h-3.5" /> : <FiLayers className="w-3.5 h-3.5" />}
          </div>
        </div>

        {/* Title & Run Info */}
        <div className="mb-6">
          <h3 className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-1 mb-1">
            {program.program_title}
          </h3>
          {program.run_name ? (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-medium text-slate-500">Run:</span>
              <span className="text-[10px] font-bold text-slate-900">{program.run_name}</span>
            </div>
          ) : (
             <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Product Master</span>
          )}
        </div>

        {/* Progress Section */}
        <div className="mt-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-slate-400">
              {program.completed_items} / {program.total_items} VERIFIED
            </span>
            <span className="text-[10px] font-black text-slate-900">{progress}%</span>
          </div>
          <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden border border-slate-200/50">
            <div 
              className="h-full bg-slate-900 transition-all duration-700 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Hover Action Indicator */}
        <div className="absolute right-4 bottom-14 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 translate-x-2 transition-all duration-300">
          <FiArrowRight className="text-blue-600 w-4 h-4" />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Premium Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-900/10">
                <FiLayers className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h1 className="text-base font-bold text-slate-900 tracking-tight">Security Checklists</h1>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[11px] font-medium text-slate-500 uppercase tracking-widest leading-none">Researcher Console</span>
                  <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                  <span className="text-[11px] font-bold text-blue-600 leading-none">{programs.length} Assigned</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 space-y-16">
        {Object.entries(runChecklistsByProgram).map(([programId, { title, runs }]) => (
          <section key={programId} className="animate-in fade-in slide-in-from-bottom-2 duration-500">
            <div className="flex items-center gap-4 mb-6 px-1">
              <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] whitespace-nowrap">
                {title}
              </h2>
              <div className="h-px bg-slate-200 w-full" />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {runs.map(run => renderProgramCard(run))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default ResearcherChecklistProgramsPage;

